import { EventEditor } from '@/features/events/editor/EventEditor';
import { ErrorState, LoadingState } from '@/components/ui/async-state';
import { AppScreen } from '@/components/ui/scene';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { useAuth } from '@/lib/auth/auth';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { getEventHref, getRouteEntityID } from '@/lib/navigation/routes';
import { useEditableEvent } from '@/features/events/useEditableEvent';
import { Stack, Redirect, router, useLocalSearchParams } from 'expo-router';

export default function EditEventScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const eventID = getRouteEntityID(id);
  const auth = useAuth();
  const { membershipsQuery } = useAccountContext();
  const eventQuery = useEditableEvent(eventID);
  const event = eventQuery.data ?? null;
  const isLoading = eventQuery.isPending;
  const error = eventQuery.isError
    ? getAPIErrorRecoveryCopy(eventQuery.error, 'event_editor', 'Unable to load this event.')
    : null;

  if (auth.isHydrating) {
    return (
      <AppScreen>
        <LoadingState label="Loading event editor..." />
      </AppScreen>
    );
  }

  if (!auth.session) {
    return <Redirect href="/auth" />;
  }

  const hasOrgVenueAccess = event
    ? membershipsQuery.data?.memberships.some(
        (membership) =>
          (membership.role === 'owner' ||
            membership.role === 'manager' ||
            membership.role === 'social') &&
          membership.venues.some((venue) => venue.venue_id === (event.venue_id ?? ''))
      ) ?? false
    : false;
  const isHost = event ? event.host.user_id === auth.session.me.user.id : false;
  const canEdit = isHost || hasOrgVenueAccess;

  if (!isLoading && (!event || !canEdit)) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <AppScreen>
          <ErrorState
            title="You cannot edit this event."
            body={error ?? 'Only the host or an authorized venue operator can update event details.'}
            actionLabel="Back to event"
            onAction={() => router.replace(eventID ? (getEventHref(eventID) as never) : '/(tabs)/events')}
          />
        </AppScreen>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <EventEditor mode="edit" eventID={eventID} initialEvent={event} isLoadingInitial={isLoading} />
    </>
  );
}
